import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { StateManager } from "./statemanager";
import { State } from "./state";
import { CharPawn } from "../pawns/charpawn";
import { GC } from "../helpers/gamecontrol";
import { NetManager } from "../helpers/netmanager";
import { MyCharacter } from "../objects/mycharacter";
import { CharObject } from "../objects/charobject";
import { NpcObject } from "../objects/npcobject";
import { MobObject } from "../objects/mobobject";
import { ItemObject } from "../objects/itemobject";
import { NpcChatDataOld } from "../loaders/con";
import { Conversation } from "../events/conversation";
import { GDM } from "../helpers/datamanager";
import { EffectManager } from "../effects/effectmanager";
import { TextureManager } from "../datamanagers/texturemanager";
import { WASDController } from "../controls/wasdcontroller";
import { InputManager } from "../controls/inputmanager";
import { platform, toNdc } from "../controls/platform";
import type { ZoneWarpPoint } from "../loaders/ifo";
import { dataUrl } from "../util/path";

// Toggle for first-person camera view
const FIRST_PERSON_VIEW = false;

// Movement mode: 'modern' = WASD, 'traditional' = click-to-move (default, set in options)
function movementMode(): 'modern' | 'traditional' {
    return GAME_OPTIONS.get('movementMode');
}

export class GameState extends State {
    mcPawnRoot: any;
    pickPos: any;
    targetGo: any;
    hoveredMob: any;
    controls: any;
    gameUI: any;
    wasdController: WASDController | null = null;
    private keysPressed = new Set<string>();
    private lastMoveSyncTime = 0;
    private lastSyncedPosition: THREE.Vector3 | null = null;
    private activeWarpContact: ZoneWarpPoint | null = null;
    private vehicleMoveSound: HTMLAudioElement | null = null;
    private vehicleMoveSoundPath: string | null = null;

    private setVehicleMoveSound(moving: boolean) {
        if (!moving) {
            this.vehicleMoveSound?.pause();
            return;
        }
        const path = MC.pawn?.isCastleGearDriving
            ? dataUrl('Sound/avata/Gear_move.wav')
            : dataUrl('Sound/Cart_Move.wav');
        if (this.vehicleMoveSoundPath !== path) {
            this.vehicleMoveSound?.pause();
            this.vehicleMoveSound = new window.Audio(path);
            this.vehicleMoveSound.loop = true;
            this.vehicleMoveSound.volume = 0.35;
            this.vehicleMoveSoundPath = path;
        }
        if (this.vehicleMoveSound?.paused) {
            void this.vehicleMoveSound.play().catch(() => {});
        }
    }

    lastTargetPosition: any = null;

    // Loading-related properties
    mapId: number;
    position: any; // THREE.Vector3
    preloadCharReceived: boolean;
    preloadCharCallback: (() => Promise<void>) | null;

    constructor() {
        super();
        this.mcPawnRoot = new THREE.Object3D();
        this.pickPos = null;
        this.targetGo = null;
        this.hoveredMob = null;
        this.mapId = 0;
        this.position = null;
        this.preloadCharReceived = false;
        this.preloadCharCallback = null;
    }

    /**
     * Set the map and position to load
     */
    setMapAndPosition(mapId: number, position: any) {
        this.mapId = mapId;
        this.position = position;
    }

    setTarget(go: any) {
        if (this.targetGo) {
            MC.target = null;
            this.targetGo.selected = false;
            this.targetGo.emit("deselected");
            this.targetGo = null;
        }

        MC.target = go.ref;
        this.targetGo = go;
        go.selected = true;
        go.emit("selected");
    }

    _setPickPos(pos: any) {
        this._clearPickPos();

        const newPickPos = new THREE.Object3D();
        this.pickPos = newPickPos;
        EffectManager.loadEffectByIdx(
            296,
            function (this: any, effect: any) {
                if (this.pickPos !== newPickPos) {
                    return;
                }

                if (effect) {
                    newPickPos.userData.effect = effect;
                    newPickPos.add(effect.rootObj);
                    newPickPos.add(effect.rootObj2);

                    effect.on("finish", () => {
                        this._clearPickPos(newPickPos);
                    });

                    effect.play();
                }

                newPickPos.position.copy(pos);
                scene.add(newPickPos);
            }.bind(this)
        );
        return newPickPos;
    }

    _clearPickPos(marker?: any) {
        if (!this.pickPos || (marker && this.pickPos !== marker)) return;
        this.pickPos.userData.effect?.stop();
        scene.remove(this.pickPos);
        this.pickPos = null;
    }

    /**
     * Override prepare to start loading tasks
     * Note: preload_char listener is set up in PreGameState to avoid race conditions
     */
    async prepare(): Promise<void> {
        // Call parent prepare to start loading tasks
        await super.prepare();
    }

    protected generateLoadingTasks() {
        return [
            {
                name: "Loading textures...",
                loader: async (onProgress: (progress: number) => void) => {
                    onProgress(0);
                    TextureManager.load("3DDATA/CONTROL/RES/CHATBOX02.DDS");
                    onProgress(100);
                },
            },
            {
                name: "Loading game data...",
                loader: async (onProgress: (progress: number) => void) => {
                    onProgress(0);
                    await new Promise<void>((resolve) => {
                        GDM.get(
                            "item_data",
                            "skill_data",
                            "list_npc",
                            "list_class",
                            "list_sell",
                            "char_motiontypes",
                            "file_sound",
                            "list_hitsound",
                            () => {
                                onProgress(100);
                                resolve();
                            }
                        );
                    });
                },
            },
            {
                name: "Loading world terrain...",
                loader: async (onProgress: (progress: number) => void) => {
                    onProgress(0);

                    const startPos =
                        this.position || new THREE.Vector3(0, 0, 0);

                    await new Promise<void>((resolve) => {
                        GZM.prepare(this.mapId, startPos, () => {
                            onProgress(60);

                            // Add world to scene so it's ready for rendering
                            GZM.addToScene();

                            // Set the world reference for NetManager
                            // (NetManager.watch was already called in PreGameState to register event listeners)
                            NetManager.world = GZM.map;

                            onProgress(100);
                            resolve();
                        });
                    });
                },
            },
            {
                name: "Loading character...",
                loader: async (onProgress: (progress: number) => void) => {
                    onProgress(0);
                    console.log("[GameState] Starting character loading task");

                    const continueLoading = async () => {
                        console.log("[GameState] continueLoading called");
                        onProgress(10);

                        // Start tracking NPC loads
                        NetManager.startInitialLoad();

                        // Join zone and create character
                        console.log("[GameState] Calling netGame.joinZone");
                        await new Promise<void>((resolve, reject) => {
                            netGame.joinZone(MC.position.z, function (data: any) {
                                console.log("[GameState] joinZone callback received");
                                if (!NetManager.verifyMapRevision(data.mapRevision)) {
                                    reject(new Error("Client/server map package revision mismatch"));
                                    return;
                                }
                                MC.hp = Math.max(0, data.curHp);
                                MC.mp = data.curMp;
                                MC.serverObjectIdx = data.objectIdx;
                                onProgress(30);

                                // Create pawn - this will preload all resources
                                MC.pawn = new CharPawn(MC);
                                MC.ingStatus.setRootObj(MC.pawn.rootObj);
                                MC.debugValidate();
                                GZM.addObject(MC);
                                GZM.setCenterObject(MC);

                                onProgress(50);

                                // Wait for character to be fully loaded
                                console.log("[GameState] Waiting for pawn to be ready");
                                MC.pawn.whenReady(() => {
                                    console.log("[GameState] Pawn ready!");
                                    resolve();
                                });
                            });
                        });

                        onProgress(70);

                        // Give server a moment to send initial NPC spawns
                        // Wait longer to ensure we capture all initial spawns
                        await new Promise(resolve => setTimeout(resolve, 500));

                        // Stop accepting new NPCs for initial load
                        NetManager.finishInitialLoad();

                        onProgress(80);

                        // Wait for all pending NPCs to load
                        console.log("[GameState] Waiting for NPCs to load");
                        await NetManager.waitForNpcLoads();
                        console.log("[GameState] All NPCs loaded!");

                        onProgress(100);
                    };

                    // Check if preload_char was already received
                    if (this.preloadCharReceived) {
                        console.log("[GameState] preload_char already received, continuing immediately");
                        await continueLoading();
                    } else {
                        // Wait for the preload_char event
                        console.log("[GameState] Waiting for preload_char event");
                        await new Promise<void>((resolve, reject) => {
                            this.preloadCharCallback = async () => {
                                console.log("[GameState] preload_char callback triggered");
                                try {
                                    await continueLoading();
                                    resolve();
                                } catch (error) {
                                    console.error("Error in continueLoading:", error);
                                    reject(error);
                                }
                            };
                        });
                    }
                },
            },
        ];
    }

    update(delta: any) {
        GZM.update(delta);

        if (!MC.isDriving) this.setVehicleMoveSound(false);

        // Safety check - controls may not be initialized yet
        if (!this.controls) {
            return;
        }

        // Update WASD movement
        if (this.wasdController) platform.pollMovementInput(this.wasdController);
        if (this.wasdController && platform.drivesMovement(movementMode())) {
            this.wasdController.update(
                delta,
                (dirX, dirY) => {
                    if (MC.isDriving) {
                        const engine = MC.inventory?.findBySlot(136);
                        if (!engine || engine.itemLife <= 0) {
                            MC._stop();
                            this.setVehicleMoveSound(false);
                            return;
                        }
                    }
                    if (MC.activeCmd && MC.activeCmd.setDirection) {
                        MC.activeCmd.setDirection(dirX, dirY);
                    } else {
                        MC._wasdMove(dirX, dirY);
                    }
                    this.setVehicleMoveSound(MC.isDriving);
                    // WASD movement is local prediction (WASDMoveCmd never
                    // talks to the network). Sync the reached position so
                    // other players see it. Throttled from the frame rate.
                    const now = Date.now();
                    if (now - this.lastMoveSyncTime > 200) {
                        this.lastMoveSyncTime = now;
                        netGame.syncPosition(MC.position.x, MC.position.y, MC.position.z);
                    }
                },
                () => {
                    MC._stop();
                    this.setVehicleMoveSound(false);
                    netGame.syncPosition(MC.position.x, MC.position.y, MC.position.z);
                },
                () => {
                    if (!MC.isDriving) MC.jump();
                },
            );
        }

        // Click-driven movement is also local prediction. The initial moveTo
        // packet names a destination; these updates report the position
        // reached so far. Shares the WASD throttle to avoid duplicate syncs.
        if (!this.lastSyncedPosition) {
            this.lastSyncedPosition = MC.position.clone();
        } else if (!this.lastSyncedPosition.equals(MC.position)) {
            const now = Date.now();
            if (now - this.lastMoveSyncTime > 200) {
                this.lastMoveSyncTime = now;
                this.lastSyncedPosition.copy(MC.position);
                netGame.syncPosition(MC.position.x, MC.position.y, MC.position.z);
            }
        }

        const warpContact = GZM.map?.findWarpContact?.(MC.position) ?? null;
        if (!warpContact) {
            this.activeWarpContact = null;
        } else if (warpContact !== this.activeWarpContact) {
            // TCP preserves this order: first commit the exact contact position
            // used by server validation, then request the IFO marker's gate ID.
            this.activeWarpContact = warpContact;
            this.lastMoveSyncTime = Date.now();
            if (this.lastSyncedPosition) {
                this.lastSyncedPosition.copy(MC.position);
            } else {
                this.lastSyncedPosition = MC.position.clone();
            }
            netGame.syncPosition(MC.position.x, MC.position.y, MC.position.z);
            netGame.enterGate(warpContact.warpId);
        }

        this.mcPawnRoot.position.copy(MC.position);
        this.mcPawnRoot.position.z += 1.6;

        // Move both camera and target by the same delta to maintain camera orientation
        if (this.lastTargetPosition) {
            const deltaPos = new THREE.Vector3().subVectors(
                this.mcPawnRoot.position,
                this.lastTargetPosition
            );
            camera.position.add(deltaPos);
            this.controls.target.add(deltaPos);
        } else {
            // First frame - just set the target
            this.controls.target.copy(this.mcPawnRoot.position);
        }
        this.lastTargetPosition = this.mcPawnRoot.position.clone();

        this.controls.update();

        // First-person view: position camera at player's head
        if (FIRST_PERSON_VIEW) {
            const headPos = MC.position.clone();
            headPos.z += 8; // Eye height
            camera.position.copy(headPos);

            // Look in the direction the player is facing
            const lookDir = new THREE.Vector3(
                Math.cos(MC.pawn.rootObj.rotation.z),
                Math.sin(MC.pawn.rootObj.rotation.z),
                0
            );
            const lookAt = headPos.clone().add(lookDir.multiplyScalar(10));
            camera.lookAt(lookAt);
            return;
        }
    }

    _startNpcTalk(npcObj: any) {
        // Use dialogIdx instead of eventIdx for NPC dialogue
        const dialogId = npcObj.dialogIdx || npcObj.eventIdx;

        if (!dialogId || dialogId === 0) {
            console.log("NPC has no dialogue (dialogIdx=0)");
            return;
        }

        console.log("Starting NPC talk with dialogIdx:", dialogId);

        GDM.get(
            "list_event",
            "quest_scripts",
            function (eventList: any, questScripts: any) {
                const eventData = eventList.row(dialogId);

                if (!eventData) {
                    console.log(
                        "Tried to start talking to an NPC with an invalid dialogue ID:",
                        dialogId
                    );
                    return;
                }

                if (eventData[3]) {
                    // TODO: Cache all this stuff
                    console.log("eventData3", eventData[3], eventData);
                    NpcChatDataOld.load(eventData[3], (convSpec: any) => {
                        console.log("convSpec", convSpec);
                        const conv = new Conversation(npcObj, convSpec, "en");
                        conv.start();
                    });
                }
            }
        );
    }

    _getMouseRay(mouseX: any, mouseY: any) {
        const mouse = new THREE.Vector3(0, 0, 0.5);
        const ndc = toNdc(mouseX, mouseY, renderer.domElement);
        mouse.x = ndc.x;
        mouse.y = ndc.y;
        mouse.unproject(camera);
        const cameraPos = camera.localToWorld(new THREE.Vector3(0, 0, 0));
        let ray = new THREE.Raycaster(
            cameraPos,
            mouse.sub(cameraPos).normalize()
        );
        return ray;
    }

    enter() {
        this.gameUI = ui.gameUI(MC);
        platform.enhanceHud(this.gameUI);

        // NetManager.watch owns the welcome_msg listener for the connection.
        // GameState only requests the message so it is rendered exactly once.
        netGame.requestWelcomeMessage();

        netGame.on("kicked", (data: any) => {
            ui.messageBox(`You have been disconnected: ${data.reason}`, ["OK"], true);
            StateManager.prepareAndSwitch("login");
        });

        // World and character are already added to scene by loading state
        // Just set up camera and controls

        // Seed the target before placing the unparented camera. Waiting for the
        // first update would position the camera near the world origin while
        // the character may already be thousands of units away.
        this.mcPawnRoot.position.copy(MC.position);
        this.mcPawnRoot.position.z += 1.6;
        scene.add(this.mcPawnRoot);

        // Camera should be in world space, not parented to player
        camera.position.set(
            this.mcPawnRoot.position.x + 4,
            this.mcPawnRoot.position.y + 4,
            this.mcPawnRoot.position.z + 2
        );
        camera.lookAt(this.mcPawnRoot.position);

        this.controls = new OrbitControls(camera, renderer.domElement);
        this.controls.target.copy(this.mcPawnRoot.position);
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.2;
        this.controls.enablePan = false;
        platform.configureCamera(this.controls);
        this.lastTargetPosition = null;

        // Set up WASD key tracking and controller. Kept active regardless of the
        // current movement mode so switching modes in the options dialog works
        // without re-entering the game state.
        InputManager.on("keydown", (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
                return;
            }
            this.keysPressed.add(e.key.toLowerCase());
        });
        InputManager.on("keyup", (e: KeyboardEvent) => {
            this.keysPressed.delete(e.key.toLowerCase());
        });
        this.wasdController = new WASDController(this.controls, this.keysPressed);
        platform.enterGame();

        InputManager.on("keydown", (e: KeyboardEvent) => {
            const target = e.target as HTMLElement;
            if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) {
                return;
            }
            const digit = parseInt(e.key, 10);
            if (digit >= 1 && digit <= 9) {
                this.gameUI.quickbar.useSlot(digit - 1);
            }
        });

        // Character is already loaded by prepare phase, just start idle animation
        if (MC.pawn) {
            MC.pawn.playIdleMotion();
        }

        InputManager.on("mousemove", (e: any) => {
            let ray = this._getMouseRay(e.clientX, e.clientY);
            let pickInfo = GZM.objectRayPick(ray);
            let cursor;
            if (pickInfo && !(pickInfo instanceof MyCharacter)) {
                if (pickInfo instanceof NpcObject) {
                    cursor = `url('${dataUrl("RES/CURSOR1.CUR")}'), pointer`;
                    // Clear hovered mob when hovering NPCs
                    if (this.hoveredMob) {
                        this.hoveredMob.emit("hover_end");
                        this.hoveredMob = null;
                    }
                } else if (pickInfo instanceof MobObject) {
                    cursor = `url('${dataUrl("RES/ATTACK.CUR")}'), pointer`;
                    // Track hovered mob (only for actual mobs, not NPCs)
                    if (this.hoveredMob !== pickInfo) {
                        if (this.hoveredMob) {
                            this.hoveredMob.emit("hover_end");
                        }
                        this.hoveredMob = pickInfo;
                        pickInfo.emit("hover_start");
                    }
                } else if (pickInfo instanceof ItemObject) {
                    cursor = `url('${dataUrl("RES/ITEM_PICK.CUR")}'), pointer`;
                } else {
                    cursor = `url('${dataUrl("RES/CURSOR1.CUR")}'), pointer`;
                }
            } else {
                cursor = `url('${dataUrl("RES/DEFAULT.CUR")}'), default`;
                // Clear hovered mob when not hovering anything
                if (this.hoveredMob) {
                    this.hoveredMob.emit("hover_end");
                    this.hoveredMob = null;
                }
            }
            $("body").css("cursor", cursor);
        });
        InputManager.on("mousedown", (e: any) => {
            e.preventDefault();

            if (e.button !== 0) {
                return;
            }

            const ray = this._getMouseRay(e.clientX, e.clientY);
            const pickInfo = GZM.rayPick(ray, (object: any) => object === MC);
            if (pickInfo) {
                if (pickInfo.object) {
                    const pickGo = pickInfo.object;
                    this.setTarget(pickGo);
                    if (pickGo instanceof MyCharacter) {
                        // Don't move to yourself or anything...
                    } else if (pickGo instanceof CharObject) {
                        GC.moveToObj(pickGo, undefined);
                    } else if (pickGo instanceof NpcObject && pickGo.isVendingProp) {
                        let moveCmd = GC.moveToObj(pickGo, undefined);
                        moveCmd.on("finish", () => {
                            MC.vending.requestBrowse(pickGo.vendingOwnerObjectIdx);
                            netGame.vendingBrowse(pickGo.vendingOwnerObjectIdx);
                        });
                    } else if (pickGo instanceof NpcObject) {
                        let moveCmd = GC.moveToObj(pickGo, undefined);
                        moveCmd.on("finish", () => {
                            console.log("Finished walk-to-object!");
                            this._startNpcTalk(pickGo);
                        });
                    } else if (pickGo instanceof MobObject) {
                        const atkCmd = GC.attackObj(pickGo);
                        atkCmd.on("finish", () => {
                            console.log("Finished Attacking!");
                        });
                    } else if (pickGo instanceof ItemObject) {
                        GC.pickupItem(pickGo);
                    }
                } else if (pickInfo.point && movementMode() === 'traditional') {
                    const moveToPos = pickInfo.point;
                    const moveCmd = GC.moveTo(moveToPos.x, moveToPos.y, moveToPos.z);
                    const marker = this._setPickPos(moveToPos);
                    moveCmd.on("finish", () => this._clearPickPos(marker));
                    moveCmd.on("leave", () => this._clearPickPos(marker));
                }
            }
        });
    }

    leave() {
        platform.leaveGame();
        this.setVehicleMoveSound(false);
        this._clearPickPos();
        if (this.gameUI) {
            this.gameUI.close();
            this.gameUI = null;
        }
        // Clean up loading-related properties
        this.preloadCharReceived = false;
        this.preloadCharCallback = null;

        // Remove world from scene
        GZM.removeFromScene();

        // Call parent leave to clean up loading state
        super.leave();
    }
}

StateManager.register("game", GameState);
